import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Calendar, Clock, User, Mail, Building2, Phone, ChevronLeft, ChevronRight, CheckCircle, MessageSquare, Loader2 } from 'lucide-react';
import { trackBookingEvent } from '../lib/tracking';
import { useLocation } from 'react-router-dom';
import { PUBLIC_IMAGES } from '../lib/publicImages';
import { EditorialImageCard } from '../components/EditorialImageCard';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AgendaPage = () => {
  const [step, setStep] = useState(1);
  const [bookingTypes, setBookingTypes] = useState([]);
  const [slots, setSlots] = useState([]);
  const [selectedType, setSelectedType] = useState(null);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedHour, setSelectedHour] = useState(null);
  const [formData, setFormData] = useState({ nombre: '', email: '', telefono: '', empresa: '', mensaje: '' });
  const [submitting, setSubmitting] = useState(false);
  const [booked, setBooked] = useState(false);
  const [bookingResult, setBookingResult] = useState(null);
  const location = useLocation();

  const loadSlots = async (bookingType = null) => {
    const query = bookingType ? `?booking_type=${bookingType}` : '';
    const response = await fetch(`${API}/booking/slots${query}`);
    const data = await response.json();
    setSlots(data.dates || []);
  };

  useEffect(() => {
    fetch(`${API}/booking/types`).then(r => r.json()).then(d => setBookingTypes(d.types)).catch(console.error);
    loadSlots().catch(console.error);
  }, []);

  useEffect(() => {
    loadSlots(selectedType).catch(console.error);
  }, [selectedType]);

  useEffect(() => {
    const requestedType = new URLSearchParams(location.search).get('type');
    if (requestedType && bookingTypes.some((type) => type.id === requestedType)) {
      setSelectedType(requestedType);
      setStep(2);
    }
  }, [location.search, bookingTypes]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const res = await fetch(`${API}/booking`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...formData,
          fecha: selectedDate,
          hora: selectedHour,
          tipo: selectedType
        })
      });
      const data = await res.json();
      if (res.ok && data.success) {
        trackBookingEvent({
          bookingType: typeInfo?.name || selectedType,
          date: selectedDate,
          hour: selectedHour
        });
        setBooked(true);
        setBookingResult(data);
      } else {
        alert(data.detail || 'Error al reservar. Inténtalo de nuevo.');
      }
    } catch {
      alert('Error de conexión. Inténtalo de nuevo.');
    } finally {
      setSubmitting(false);
    }
  };

  const selectedDateSlots = slots.find(s => s.date === selectedDate);
  const typeInfo = bookingTypes.find(t => t.id === selectedType);

  if (booked) {
    return (
      <div className="min-h-screen bg-background pt-24 pb-golden-2xl flex items-center justify-center">
        <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }}
          className="max-w-lg mx-auto px-golden-md text-center">
          <div className="glass-card p-golden-2xl">
            <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ delay: 0.2, type: "spring" }}
              className="w-24 h-24 mx-auto mb-golden-lg rounded-full bg-gradient-to-br from-primary/20 to-secondary/20 flex items-center justify-center">
              <CheckCircle className="w-12 h-12 text-primary" />
            </motion.div>
            <h2 className="text-2xl mb-golden-md">Cita <span className="gradient-text">confirmada</span></h2>
            <div className="bg-card p-golden-md rounded-xl mb-golden-lg text-left">
              <p className="text-text-muted text-sm mb-2">Detalles de tu cita:</p>
              <p className="text-text-heading font-semibold">{typeInfo?.name}</p>
              <p className="text-primary text-lg font-bold">{selectedDate} a las {selectedHour}</p>
              <p className="text-text-muted text-sm mt-2">{typeInfo?.duration} minutos</p>
              {bookingResult?.consultant_name && <p className="text-text-body text-sm mt-2" data-testid="booking-confirmed-consultant">Consultor asignado: <span className="text-text-heading font-medium">{bookingResult.consultant_name}</span></p>}
            </div>
            <p className="text-text-body mb-golden-lg">
              {bookingResult?.consultant_name
                ? 'Te enviaremos un email con los detalles y el consultor humano asignado para esta primera sesión.'
                : 'Te enviaremos un email de confirmación con los detalles y el enlace para la reunión.'}
            </p>
            <button onClick={() => { setBooked(false); setStep(1); setSelectedType(null); setSelectedDate(null); setSelectedHour(null); }}
              className="btn-secondary" data-testid="book-another">
              Reservar otra cita
            </button>
          </div>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background pt-24 pb-golden-2xl">
      <div className="max-w-4xl mx-auto px-golden-md lg:px-golden-lg">
        <div className="text-center mb-golden-xl">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/10 border border-primary/20 mb-golden-lg">
            <Calendar className="w-4 h-4 text-primary" />
            <span className="text-sm text-primary font-medium">Consultoría estratégica · 69 USD</span>
          </motion.div>
          <motion.h1 initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="mb-golden-md">
            Agenda tu <span className="gradient-text">consultoría estratégica</span>
          </motion.h1>
          <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}
            className="text-lg text-text-body max-w-2xl mx-auto">
            Reserva una sesión de entrada con Neuralia369 por <span className="text-primary font-semibold">69 USD</span>. Se asigna automáticamente a un consultor humano disponible para analizar tu negocio y definir el siguiente mejor paso.
          </motion.p>
        </div>

        <div className="mb-golden-xl">
          <EditorialImageCard
            image={PUBLIC_IMAGES.agenda.hero}
            alt="Consultoría estratégica humana en entorno ejecutivo"
            kicker="Primera conversación"
            title="Una sesión humana, clara y con intención estratégica desde el inicio"
            description="La primera toma de contacto no se siente automática: se siente bien preparada, ejecutiva y útil para tomar decisiones." 
            testId="agenda-editorial-image"
            minHeight="min-h-[22rem]"
          />
        </div>

        {/* Progress */}
        <div className="flex items-center justify-center gap-4 mb-golden-xl">
          {[1, 2, 3].map(s => (
            <div key={s} className="flex items-center gap-2">
              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold transition-all ${
                step >= s ? 'bg-primary text-background' : 'bg-card text-text-muted border border-border-subtle'
              }`}>{s}</div>
              <span className={`hidden sm:block text-sm ${step >= s ? 'text-primary' : 'text-text-muted'}`}>
                {s === 1 ? 'Tipo' : s === 2 ? 'Fecha y hora' : 'Datos'}
              </span>
              {s < 3 && <ChevronRight className="w-4 h-4 text-text-muted" />}
            </div>
          ))}
        </div>

        <AnimatePresence mode="wait">
          {/* Step 1: Select type */}
          {step === 1 && (
            <motion.div key="step1" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }}
              className="grid md:grid-cols-3 gap-golden-lg">
              {bookingTypes.map((type) => (
                <button key={type.id} onClick={() => { setSelectedType(type.id); setStep(2); }}
                  className={`glass-card p-golden-xl text-left group hover:border-primary/50 transition-all ${selectedType === type.id ? 'border-primary ring-2 ring-primary/20' : ''}`}
                  data-testid={`booking-type-${type.id}`}>
                  <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center mb-golden-md group-hover:scale-110 transition-transform">
                    <Calendar className="w-6 h-6 text-primary" />
                  </div>
                  <h3 className="text-lg mb-golden-sm">{type.name}</h3>
                  <p className="text-text-body text-sm mb-golden-md">{type.description}</p>
                  {type.price && <p className="text-text-heading text-sm font-semibold mb-golden-md" data-testid={`booking-type-price-${type.id}`}>${type.price} USD</p>}
                  <div className="mb-golden-md" data-testid={`booking-type-mode-${type.id}`}>
                    <span className={`rounded-full px-3 py-1 text-[10px] uppercase tracking-[0.16em] ${type.human_consultant_required ? 'border border-primary/20 bg-primary/10 text-primary' : 'border border-border-subtle bg-card text-text-muted'}`}>
                      {type.human_consultant_required ? 'Consultor humano' : 'Reserva automática'}
                    </span>
                  </div>
                  <p className="text-primary font-semibold flex items-center gap-1">
                    <Clock className="w-4 h-4" /> {type.duration} minutos
                  </p>
                </button>
              ))}
            </motion.div>
          )}

          {/* Step 2: Select date and time */}
          {step === 2 && (
            <motion.div key="step2" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }}>
              <div className="grid md:grid-cols-2 gap-golden-lg">
                <div className="glass-card p-golden-lg">
                  <h3 className="text-lg mb-golden-md flex items-center gap-2">
                    <Calendar className="w-5 h-5 text-primary" /> Selecciona fecha
                  </h3>
                  <div className="space-y-2 max-h-[400px] overflow-y-auto">
                    {slots.filter(s => s.available_slots.length > 0).map(slot => (
                      <button key={slot.date} onClick={() => { setSelectedDate(slot.date); setSelectedHour(null); }}
                        className={`w-full p-3 rounded-xl text-left transition-all flex items-center justify-between ${
                          selectedDate === slot.date ? 'bg-primary/20 border border-primary/40' : 'bg-card hover:bg-card-hover border border-border-subtle'
                        }`} data-testid={`date-${slot.date}`}>
                        <div>
                          <p className="font-medium text-text-heading">{slot.date}</p>
                          <p className="text-xs text-text-muted capitalize">{slot.day_name}</p>
                        </div>
                        <span className="text-xs text-primary">{slot.available_slots.length} disponibles</span>
                      </button>
                    ))}
                  </div>
                </div>

                <div className="glass-card p-golden-lg">
                  <h3 className="text-lg mb-golden-md flex items-center gap-2">
                    <Clock className="w-5 h-5 text-primary" /> Selecciona hora
                  </h3>
                  {selectedDate ? (
                    <div className="grid grid-cols-3 gap-2">
                      {selectedDateSlots?.available_slots?.map(hour => (
                        <button key={hour} onClick={() => setSelectedHour(hour)}
                          className={`p-3 rounded-xl text-center font-medium transition-all ${
                            selectedHour === hour ? 'bg-primary text-background' : 'bg-card hover:bg-card-hover text-text-body border border-border-subtle'
                          }`} data-testid={`hour-${hour}`}>
                          {hour}
                        </button>
                      ))}
                    </div>
                  ) : (
                    <p className="text-text-muted text-center py-golden-xl">Primero selecciona una fecha</p>
                  )}
                </div>
              </div>

              <div className="flex justify-between mt-golden-lg">
                <button onClick={() => setStep(1)} className="btn-secondary">
                  <ChevronLeft className="w-5 h-5" /> Anterior
                </button>
                <button onClick={() => setStep(3)} disabled={!selectedDate || !selectedHour}
                  className="btn-primary disabled:opacity-50" data-testid="step2-next">
                  Siguiente <ChevronRight className="w-5 h-5" />
                </button>
              </div>
            </motion.div>
          )}

          {/* Step 3: Personal info */}
          {step === 3 && (
            <motion.div key="step3" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }}>
              <div className="glass-card p-golden-xl max-w-2xl mx-auto">
                <div className="bg-card p-golden-md rounded-xl mb-golden-lg">
                  <p className="text-text-muted text-sm">Tu cita:</p>
                  <p className="text-text-heading font-semibold">{typeInfo?.name} - {typeInfo?.duration} min</p>
                  {typeInfo?.price && <p className="text-text-body text-sm mt-1" data-testid="booking-summary-price">Inversión: <span className="text-primary font-semibold">${typeInfo.price} USD</span></p>}
                  <p className="text-primary font-bold">{selectedDate} a las {selectedHour}</p>
                  {typeInfo?.human_consultant_required && <p className="text-text-body text-sm mt-2" data-testid="booking-human-consultant-note">Asignaremos automáticamente un consultor humano disponible para esta primera sesión.</p>}
                </div>
                <form onSubmit={handleSubmit} className="space-y-golden-lg" data-testid="booking-form">
                  <div className="grid md:grid-cols-2 gap-golden-lg">
                    <div>
                      <label className="block text-text-body mb-golden-sm text-sm">
                        <User className="w-4 h-4 inline mr-1" /> Nombre *
                      </label>
                      <input type="text" className="input-kinesthetic" required value={formData.nombre}
                        onChange={e => setFormData({...formData, nombre: e.target.value})} data-testid="booking-nombre" />
                    </div>
                    <div>
                      <label className="block text-text-body mb-golden-sm text-sm">
                        <Mail className="w-4 h-4 inline mr-1" /> Email *
                      </label>
                      <input type="email" className="input-kinesthetic" required value={formData.email}
                        onChange={e => setFormData({...formData, email: e.target.value})} data-testid="booking-email" />
                    </div>
                  </div>
                  <div className="grid md:grid-cols-2 gap-golden-lg">
                    <div>
                      <label className="block text-text-body mb-golden-sm text-sm">
                        <Phone className="w-4 h-4 inline mr-1" /> Teléfono
                      </label>
                      <input type="tel" className="input-kinesthetic" value={formData.telefono}
                        onChange={e => setFormData({...formData, telefono: e.target.value})} data-testid="booking-telefono" />
                    </div>
                    <div>
                      <label className="block text-text-body mb-golden-sm text-sm">
                        <Building2 className="w-4 h-4 inline mr-1" /> Empresa
                      </label>
                      <input type="text" className="input-kinesthetic" value={formData.empresa}
                        onChange={e => setFormData({...formData, empresa: e.target.value})} data-testid="booking-empresa" />
                    </div>
                  </div>
                  <div>
                    <label className="block text-text-body mb-golden-sm text-sm">
                      <MessageSquare className="w-4 h-4 inline mr-1" /> Mensaje (opcional)
                    </label>
                    <textarea className="input-kinesthetic min-h-[100px] resize-none" value={formData.mensaje}
                      onChange={e => setFormData({...formData, mensaje: e.target.value})}
                      placeholder="Cuéntanos brevemente sobre tu proyecto..." data-testid="booking-mensaje" />
                  </div>
                  <div className="flex justify-between">
                    <button type="button" onClick={() => setStep(2)} className="btn-secondary">
                      <ChevronLeft className="w-5 h-5" /> Anterior
                    </button>
                    <button type="submit" disabled={submitting} className="btn-primary" data-testid="booking-submit">
                      {submitting ? <><Loader2 className="w-5 h-5 animate-spin" /> Reservando...</>
                        : <><CheckCircle className="w-5 h-5" /> Confirmar cita</>}
                    </button>
                  </div>
                </form>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};

export default AgendaPage;
